/**
 * <s-signup-popup> — timed email capture dialog.
 * - Opens once after data-delay seconds unless dismissed within data-remember-days.
 * - Reopens immediately when the page re-renders with a signup result message.
 * - Never auto-opens in the theme editor (data-design-mode).
 */
const STORAGE_KEY = 'kageform:protocol-popup';

class SSignupPopup extends HTMLElement {
  #dialog;
  #timer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    // Label the dialog with the first heading in its body.
    const heading = this.#dialog.querySelector('h1, h2, h3');
    const titleId = this.#dialog.querySelector('[data-signup-title-id]')?.dataset.signupTitleId;
    if (heading && titleId && !heading.id) heading.id = titleId;

    this.#dialog.addEventListener('close', () => this.#remember());
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) {
        this.#dialog.close();
        return;
      }

      const copyButton = event.target instanceof Element ? event.target.closest('[data-copy-code]') : null;
      if (copyButton) this.#copyCode(copyButton);
    });

    const result = this.#dialog.querySelector('[data-signup-message]');
    if (result) {
      if (result.dataset.signupMessage === 'success') this.#remember(365);
      this.#open();
      result.focus({ preventScroll: true });
      return;
    }

    if (this.hasAttribute('data-design-mode') || this.#isRemembered()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = window.setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
  }

  #open() {
    if (this.#dialog.open || document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }

  async #copyCode(button) {
    const code = button.dataset.copyCode;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Clipboard blocked: select the code so the buyer can copy it manually.
      const codeElement = this.#dialog.querySelector('[data-signup-code]');
      if (codeElement) window.getSelection()?.selectAllChildren(codeElement);
      return;
    }

    const original = button.textContent;
    if (button.dataset.copiedLabel) button.textContent = button.dataset.copiedLabel;
    window.setTimeout(() => { button.textContent = original; }, 2000);
  }

  #remember(days = Number(this.dataset.rememberDays) || 14) {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 86400000));
    } catch {
      /* storage unavailable: the pop-up simply shows again next visit */
    }
  }

  #isRemembered() {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) > Date.now();
    } catch {
      return false;
    }
  }
}

if (!customElements.get('s-signup-popup')) customElements.define('s-signup-popup', SSignupPopup);
